import { useEffect } from 'react';
import { createSelector } from 'reselect';
import { useRegisterPersistedStore } from '@/stores/PersistenceProvider';
import { LOGIC, LOGIC_KEY, NO_MATCH_PROPERTY } from '@/tools/compositeSearch/compositeSearch.constants';
import {
  buildCompositeSearchConfigParams,
  buildCompositeSearchFormula,
  correctLegacySelectOperators,
  deriveStateFromFormula,
  isMaximumDurationOverrideRequired,
  toFrontendMaximumDuration,
} from '@/tools/compositeSearch/compositeSearch.utilities';
import { BASE_TOOL_COMMON_PROPS } from '@/tools/tool.constants';
import { buildToolRehydrationState, sanitizeToolItem } from '@/tools/tool.utilities';
import { type ActionsFactory, createStore, createStoreInstance } from '@seeqdev/utils';
import type { FrontendDuration } from '@/services/systemConfiguration.types';
import type {
  BaseToolPersistedState,
  DurationWithUnits,
  ParameterDefinitions,
  ToolConditionMetadata,
  ToolItem,
  ToolRehydratePayload,
} from '@/tools/tool.types';

export interface CompositeSearchState extends BaseToolPersistedState {
  /** The condition selected for the "A" side of the logic operator. */
  conditionA: ToolItem | undefined;
  /** The condition selected for the "B" side of the logic operator. */
  conditionB: ToolItem | undefined;
  /** The hex color used for the output condition. */
  color: string;
  /** The key of the selected logic operator, e.g. `UNION`, `JOIN`. One of `LOGIC_KEY`. */
  selectedOperator: string | undefined;
  /** User-supplied maximum duration override for condition A, used when A is unbounded. */
  maximumDurationOverrideA: FrontendDuration | undefined;
  /** User-supplied maximum duration override for condition B, used when B is unbounded. */
  maximumDurationOverrideB: FrontendDuration | undefined;
  /** True when this is a pre-Conditions 2.0 Union condition that still carries a maximum duration. */
  isUpgradedUnionCondition: boolean;
  /** Whether Join should include A's capsule (Join logic only). */
  inclusiveA: boolean;
  /** Whether Join should include B's capsule (Join logic only). */
  inclusiveB: boolean;
  /** The capsule property to match on, or `NO_MATCH_PROPERTY` when none is selected. */
  matchProperty: string;
  /** Whether Intersection/Join should keep the input capsules' properties on the output. */
  keepProperties: boolean;
  /** Whether Join should use the earliest of A/B's start time for the output capsule. */
  useEarliestStart: boolean;
}

export type CompositeSearchDehydratedState = CompositeSearchState;

interface CompositeSearchRehydratePayload extends ToolRehydratePayload {
  /** Legacy field: some pre-formula-based composite searches persisted the operator directly in UIConfig. */
  selectedOperator?: string;
  conditionMetadata?: ToolConditionMetadata;
}

export interface CompositeSearchActions {
  /** Sets the condition selected for the "A" side of the logic operator. */
  setConditionA: (item: ToolItem | null | undefined) => void;
  /** Sets the condition selected for the "B" side of the logic operator. */
  setConditionB: (item: ToolItem | null | undefined) => void;
  /** Sets the hex color used for the output condition. */
  setColor: (color: string) => void;
  /** Sets the selected logic operator and resets the output maximum duration to the given system default. */
  setOperator: (operator: string | undefined, defaultMaximumDuration?: DurationWithUnits) => void;
  /** Controls whether A's capsule is included in the Join output. */
  setInclusiveA: (inclusiveA: boolean) => void;
  /** Controls whether B's capsule is included in the Join output. */
  setInclusiveB: (inclusiveB: boolean) => void;
  /** Sets the maximum duration override for condition A. */
  setConditionAMaximumDurationOverride: (maximumDuration: FrontendDuration | null | undefined) => void;
  /** Sets the maximum duration override for condition B. */
  setConditionBMaximumDurationOverride: (maximumDuration: FrontendDuration | null | undefined) => void;
  /** Sets the output maximum duration. Passing `null` for `value` clears it. */
  setMaximumDuration: (value: number | null, units: string | null) => void;
  /** Sets whether Join should use the earliest of A/B's start time for the output capsule. */
  setUseEarliestStart: (useEarliestStart: boolean) => void;
  /** Sets whether Intersection/Join should keep the input capsules' properties on the output. */
  setKeepProperties: (keepProperties: boolean) => void;
  /** Sets the capsule property to match on. */
  setMatchProperty: (matchProperty: string) => void;
  /** Sets the user-visible name of the output condition. */
  setSearchName: (name: string) => void;
  /** Collapses or expands the Advanced Parameters section. */
  setAdvancedParametersCollapsed: (collapsed: boolean) => void;
  /** Swaps conditions A and B, along with their maximum duration overrides. */
  swapConditions: () => void;
  /** Clears the pre-Conditions 2.0 Union upgrade flag. */
  unsetUpgradedUnionCondition: () => void;
  /** Rehydrates the store from a persisted tool-for-edit payload. */
  rehydrateForEdit: (payload: CompositeSearchRehydratePayload) => void;
  /** Resets the store to its initial state, applying any supplied overrides on top of it. */
  reset: (overrides?: Partial<CompositeSearchState>) => void;
}

const parameterDefinitions: ParameterDefinitions = {
  conditionA: { predicate: ['name', 'a'] },
  conditionB: { predicate: ['name', 'b'] },
};

const initialState: CompositeSearchState = Object.freeze({
  ...BASE_TOOL_COMMON_PROPS,
  conditionA: undefined,
  conditionB: undefined,
  color: '',
  selectedOperator: undefined,
  maximumDurationOverrideA: undefined,
  maximumDurationOverrideB: undefined,
  isUpgradedUnionCondition: false,
  inclusiveA: true,
  inclusiveB: true,
  matchProperty: NO_MATCH_PROPERTY,
  keepProperties: false,
  useEarliestStart: true,
});

export const dehydrateCompositeSearchState = (state: CompositeSearchState): CompositeSearchDehydratedState => state;

const compositeSearchActionsFactory: ActionsFactory<CompositeSearchState, CompositeSearchActions> = (
  set,
  _get,
  resetState,
) => ({
  setConditionA: (item) =>
    set((draft) => {
      draft.conditionA = sanitizeToolItem(item) ?? undefined;
    }),

  setConditionB: (item) =>
    set((draft) => {
      draft.conditionB = sanitizeToolItem(item) ?? undefined;
    }),

  setColor: (color) =>
    set((draft) => {
      draft.color = color;
    }),

  setOperator: (operator, defaultMaximumDuration) =>
    set((draft) => {
      draft.selectedOperator = operator;
      draft.maximumDuration = defaultMaximumDuration ?? undefined;
    }),

  setInclusiveA: (inclusiveA) =>
    set((draft) => {
      draft.inclusiveA = inclusiveA;
    }),

  setInclusiveB: (inclusiveB) =>
    set((draft) => {
      draft.inclusiveB = inclusiveB;
    }),

  setConditionAMaximumDurationOverride: (maximumDuration) =>
    set((draft) => {
      draft.maximumDurationOverrideA = maximumDuration ?? undefined;
    }),

  setConditionBMaximumDurationOverride: (maximumDuration) =>
    set((draft) => {
      draft.maximumDurationOverrideB = maximumDuration ?? undefined;
    }),

  setMaximumDuration: (value, units) =>
    set((draft) => {
      // Units come from a constrained duration-unit picker, so they're always valid moment duration units.
      draft.maximumDuration =
        typeof value === 'number' && units !== null ? ({ value, units } as DurationWithUnits) : undefined;
    }),

  setUseEarliestStart: (useEarliestStart) =>
    set((draft) => {
      draft.useEarliestStart = useEarliestStart;
    }),

  setKeepProperties: (keepProperties) =>
    set((draft) => {
      draft.keepProperties = keepProperties;
    }),

  setMatchProperty: (matchProperty) =>
    set((draft) => {
      draft.matchProperty = matchProperty;
    }),

  setSearchName: (name) =>
    set((draft) => {
      draft.name = name;
    }),

  setAdvancedParametersCollapsed: (collapsed) =>
    set((draft) => {
      draft.advancedParametersCollapsed = collapsed;
    }),

  swapConditions: () =>
    set((draft) => {
      [draft.conditionA, draft.conditionB] = [draft.conditionB, draft.conditionA];
      [draft.maximumDurationOverrideA, draft.maximumDurationOverrideB] = [
        draft.maximumDurationOverrideB,
        draft.maximumDurationOverrideA,
      ];
    }),

  unsetUpgradedUnionCondition: () =>
    set((draft) => {
      draft.isUpgradedUnionCondition = false;
    }),

  rehydrateForEdit: (payload) =>
    set((draft) => {
      const corrected = correctLegacySelectOperators(payload.selectedOperator, payload.parameters ?? []);

      const rehydrationState = buildToolRehydrationState({
        payload: { ...payload, parameters: corrected.parameters },
        parameterDefinitions,
        currentAdvancedParametersCollapsed: draft.advancedParametersCollapsed,
      });

      // Merge `initialState` first to reset the store, then overlay the rehydrated values — this is the
      // equivalent of calling `resetState()` but in a single `set()` so subscribers only receive one update.
      Object.assign(
        draft,
        initialState,
        rehydrationState.config,
        rehydrationState.selectedParameters,
        {
          originalParameters: rehydrationState.originalParameters,
          // Legacy pre-formula-based composite searches persisted the operator directly; modern items always
          // carry a formula and have this overwritten below.
          selectedOperator: corrected.selectedOperator,
          maximumDuration: toFrontendMaximumDuration(payload.conditionMetadata?.maximumDuration) as unknown as
            | DurationWithUnits
            | undefined,
        },
        payload.formula ? deriveStateFromFormula(payload.formula) : undefined,
      );
    }),

  reset: (overrides) => resetState(overrides),
});

const STORE_NAME = 'CompositeSearch';

const [CompositeSearchStoreProvider, useCompositeSearchStoreInstance, useCompositeSearchSelector] = createStore(
  STORE_NAME,
  initialState,
  compositeSearchActionsFactory,
);

export { CompositeSearchStoreProvider };

export const useCompositeSearchId = () => useCompositeSearchSelector((state) => state.id);
export const useCompositeSearchName = () => useCompositeSearchSelector((state) => state.name);
export const useCompositeSearchOriginalParameters = () =>
  useCompositeSearchSelector((state) => state.originalParameters);
export const useCompositeSearchColor = () => useCompositeSearchSelector((state) => state.color);
export const useCompositeSearchConditionA = () => useCompositeSearchSelector((state) => state.conditionA);
export const useCompositeSearchConditionB = () => useCompositeSearchSelector((state) => state.conditionB);
export const useCompositeSearchSelectedOperator = () => useCompositeSearchSelector((state) => state.selectedOperator);
export const useCompositeSearchInclusiveA = () => useCompositeSearchSelector((state) => state.inclusiveA);
export const useCompositeSearchInclusiveB = () => useCompositeSearchSelector((state) => state.inclusiveB);
export const useCompositeSearchIsUpgradedUnionCondition = () =>
  useCompositeSearchSelector((state) => state.isUpgradedUnionCondition);
export const useCompositeSearchUseEarliestStart = () => useCompositeSearchSelector((state) => state.useEarliestStart);
export const useCompositeSearchKeepProperties = () => useCompositeSearchSelector((state) => state.keepProperties);
export const useCompositeSearchMatchProperty = () => useCompositeSearchSelector((state) => state.matchProperty);
export const useCompositeSearchAdvancedParametersCollapsed = () =>
  useCompositeSearchSelector((state) => state.advancedParametersCollapsed);

export const useCompositeSearchMaximumDuration = () => useCompositeSearchSelector((state) => state.maximumDuration);

export const useCompositeSearchMaximumDurationOverrideA = () =>
  useCompositeSearchSelector((state) => state.maximumDurationOverrideA);

export const useCompositeSearchMaximumDurationOverrideB = () =>
  useCompositeSearchSelector((state) => state.maximumDurationOverrideB);

/** Whether an input maximum duration override control should be shown for condition A. */
export const useCompositeSearchMaximumDurationOverrideRequiredA = (
  maximumDurationOverrideA: FrontendDuration | undefined,
) =>
  useCompositeSearchSelector((state) =>
    isMaximumDurationOverrideRequired(
      'A',
      state.selectedOperator,
      !!state.conditionA,
      maximumDurationOverrideA,
      state.keepProperties,
      state.matchProperty,
    ),
  );

/** Whether an input maximum duration override control should be shown for condition B. */
export const useCompositeSearchMaximumDurationOverrideRequiredB = (
  maximumDurationOverrideB: FrontendDuration | undefined,
) =>
  useCompositeSearchSelector((state) =>
    isMaximumDurationOverrideRequired('B', state.selectedOperator, !!state.conditionB, maximumDurationOverrideB),
  );

/** Whether the output maximum duration control should be shown. */
export const useCompositeSearchMaximumDurationRequired = () =>
  useCompositeSearchSelector((state) => {
    const logic = LOGIC.find(({ key }) => key === state.selectedOperator);
    // to ensure the maximumDurationRequired property is correct the currently selected operator must be
    // considered (CRAB-14553)
    return logic?.withMaximumDuration ?? (state.isUpgradedUnionCondition && state.selectedOperator === LOGIC_KEY.UNION);
  });

/** The formula and parameters (condition A/B ids) this tool's current state represents, or `undefined` when the
 * state doesn't have enough information yet. */
const selectCompositeSearchFormulaWithParameters = createSelector(
  (state: CompositeSearchState) => state,
  buildCompositeSearchFormula,
);
export const useCompositeSearchFormulaWithParameters = () =>
  useCompositeSearchSelector(selectCompositeSearchFormulaWithParameters);

/** The persisted UIConfig for this tool. Composite search gets everything it needs from the formula/parameters,
 * so only `type` and `advancedParametersCollapsed` are kept. */
const selectCompositeSearchConfigParams = createSelector(
  (state: CompositeSearchState) => state,
  buildCompositeSearchConfigParams,
);
export const useCompositeSearchConfigParams = () => useCompositeSearchSelector(selectCompositeSearchConfigParams);

/** Returns the CompositeSearch store actions for updating state. */
export const useCompositeSearchActions = () => useCompositeSearchStoreInstance().actions;

/**
 * @deprecated For legacy singleton bridge only. Non-React consumers (action files) use the singleton created from
 * this in core.stores.ts. DO NOT use in new code.
 */
export function legacyCreateCompositeSearchStore() {
  return createStoreInstance<CompositeSearchState, CompositeSearchActions>(
    initialState,
    compositeSearchActionsFactory,
    { storeName: STORE_NAME },
  );
}

export const usePersistCompositeSearchStore = () => {
  const register = useRegisterPersistedStore();
  const store = useCompositeSearchStoreInstance();

  useEffect(() => {
    return register({
      storeName: 'sqCompositeSearchStore',
      persistenceLevel: 'WORKSHEET',
      store,
      dehydrate: dehydrateCompositeSearchState,
    });
  }, [register, store]);
};
